import React, { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { 
  Home, BarChart3, Cpu, Bell, Settings, Menu, X, 
  Sun, Moon, Activity, TrendingUp, AlertTriangle, CheckCircle, MapPin
} from 'lucide-react';
import DemoPage from './DemoPage';
import DemoAnalytics from './DemoAnalytics';
import DemoAlerts from './DemoAlerts';
import DemoSettings from './DemoSettings';
import DemoLocations from './DemoLocations';
import './DemoApp.css';

type ViewType = 'dashboard' | 'analytics' | 'alerts' | 'locations' | 'settings';

const DemoApp: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [darkMode, setDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalDevices: 18,
    activeDevices: 17,
    activeAlerts: 3,
    systemStatus: 'healthy' as 'healthy' | 'warning' | 'error'
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    // Fetch stats with delay to avoid rate limiting
    const fetchStats = async () => {
      try {
        // Add delay before fetching (plus long pour éviter conflits)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/demo/analytics/summary`);
        if (response.ok) {
          const data = await response.json();
          setStats({
            totalDevices: data.totalDevices || 18,
            activeDevices: data.activeDevices || 17,
            activeAlerts: data.alertsCount || 3,
            systemStatus: data.uptime > 95 ? 'healthy' : data.uptime > 80 ? 'warning' : 'error'
          });
        } else if (response.status === 429) {
          console.warn('Rate limited, will retry in 10 seconds');
          setTimeout(fetchStats, 10000);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };

    // Initial fetch with longer delay (attendre que les autres pages chargent)
    setTimeout(fetchStats, 3000);
    
    // Refresh every 3 minutes
    const interval = setInterval(fetchStats, 180000);
    return () => clearInterval(interval);
  }, []);

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
    setSidebarOpen(false);
    window.location.hash = `demo-${view}`;
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const renderView = () => {
    switch (currentView) {
      case 'analytics':
        return <DemoAnalytics />;
      case 'alerts':
        return <DemoAlerts />;
      case 'locations':
        return <DemoLocations />;
      case 'settings':
        return <DemoSettings />;
      default:
        return <DemoPage />;
    }
  };

  const navItems = [
    { id: 'dashboard', name: 'Tableau de Bord', icon: Home, desc: 'Vue d\'ensemble' },
    { id: 'analytics', name: 'Analyses', icon: BarChart3, desc: 'Statistiques' },
    { id: 'alerts', name: 'Alertes', icon: Bell, desc: 'Notifications', badge: stats.activeAlerts },
    { id: 'locations', name: 'Localisations', icon: MapPin, desc: 'Gestion des sites' },
    { id: 'settings', name: 'Paramètres', icon: Settings, desc: 'Configuration' }
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <Toaster position="top-right" />
      
      {/* Sidebar Desktop */}
      <aside className={`hidden lg:flex lg:flex-col w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-3xl">🌾</span>
            <span>IoT Agricole</span>
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Plateforme de surveillance</p>
        </div>

        {/* Stats Summary */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 mb-1">
                <Cpu className="h-4 w-4" />
                <span className="text-xs font-semibold">Appareils</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {stats.activeDevices}/{stats.totalDevices}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${
              stats.activeAlerts === 0 ? 'bg-green-50 dark:bg-green-900/20' : 
              stats.activeAlerts < 5 ? 'bg-yellow-50 dark:bg-yellow-900/20' : 
              'bg-red-50 dark:bg-red-900/20'
            }`}>
              <div className={`flex items-center gap-2 mb-1 ${
                stats.activeAlerts === 0 ? 'text-green-600 dark:text-green-400' : 
                stats.activeAlerts < 5 ? 'text-yellow-600 dark:text-yellow-400' : 
                'text-red-600 dark:text-red-400'
              }`}>
                <Bell className="h-4 w-4" />
                <span className="text-xs font-semibold">Alertes</span>
              </div>
              <p className={`text-2xl font-bold ${
                stats.activeAlerts === 0 ? 'text-green-700 dark:text-green-300' : 
                stats.activeAlerts < 5 ? 'text-yellow-700 dark:text-yellow-300' : 
                'text-red-700 dark:text-red-300'
              }`}>
                {stats.activeAlerts}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleViewChange(item.id as ViewType)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5" />
                    <div className="text-left">
                      <div className="font-semibold">{item.name}</div>
                      <div className={`text-xs ${isActive ? 'text-emerald-100' : 'text-gray-500 dark:text-gray-400'}`}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="px-2 py-1 text-xs font-bold bg-red-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
          {/* Bouton retour */}
          <a
            href="#"
            onClick={() => window.location.hash = ''}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors font-semibold shadow-lg"
          >
            <span>←</span>
            <span>Interface Production</span>
          </a>
          
          <button
            onClick={toggleDarkMode}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            <span className="font-semibold">{darkMode ? 'Mode Clair' : 'Mode Sombre'}</span>
          </button>
          <div className="flex items-center justify-center gap-2 text-sm">
            <div className={`h-2 w-2 rounded-full ${
              stats.systemStatus === 'healthy' ? 'bg-green-500' :
              stats.systemStatus === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
            } animate-pulse`}></div>
            <span className="text-gray-600 dark:text-gray-400">Système opérationnel</span>
          </div>
        </div>
      </aside>

      {/* Sidebar Mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white dark:bg-gray-800 shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-2xl">🌾</span>
                <span>IoT Agricole</span>
              </h1>
              <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="p-4">
              <div className="space-y-2">
                {navItems.map(item => {
                  const Icon = item.icon;
                  const isActive = currentView === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleViewChange(item.id as ViewType)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-emerald-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div>
                          <h2 className="card-title">🌱 IoT Agricole</h2>
                          <p className="text-sm" style={{color: 'var(--text-secondary)'}}>
                            Système de surveillance
                          </p>
                        </div>
                        <Icon className="h-5 w-5" />
                        <span className="font-semibold">{item.name}</span>
                      </div>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="px-2 py-1 text-xs font-bold bg-red-500 text-white rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Top Bar Mobile */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">IoT Agricole</h1>
          <button
            onClick={toggleDarkMode}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {renderView()}
        </div>
      </main>
    </div>
  );
};

export default DemoApp;
